/**
 * Cookie Utility Functions for GDPR Compliance
 * 
 * These utilities handle comprehensive cookie deletion to ensure
 * true GDPR compliance when users withdraw consent.
 */

/**
 * Delete a cookie by trying all possible paths and domains
 * Cookies can be set on various paths and domains, so we need to try multiple combinations
 */
export function deleteCookie(name: string) {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;
  const paths = ['/', pathname, pathname.substring(0, pathname.lastIndexOf('/'))];
  
  // Extract possible domains (e.g., "example.com" and ".example.com" from "sub.example.com")
  const domains = [
    hostname,
    '.' + hostname,
    hostname.substring(hostname.indexOf('.')),
    hostname.substring(hostname.lastIndexOf('.')),
    '',
    null
  ];

  // Try deleting the cookie with various combinations
  paths.forEach(path => {
    domains.forEach(domain => {
      // Standard cookie deletion
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
      
      if (domain) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain};`;
      }
      
      // Also try with Secure and SameSite flags
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; SameSite=None; Secure`;
      
      if (domain) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}; SameSite=None; Secure`;
      }
    });
  });
}

/**
 * Delete all cookies matching a pattern
 * @param patterns - Array of patterns to match (supports wildcards with *)
 */
export function deleteCookiesByPattern(patterns: string[]) {
  const cookies = document.cookie.split(';');
  
  cookies.forEach(cookie => {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
    
    // Check if cookie name matches any pattern
    const shouldDelete = patterns.some(pattern => {
      const regex = new RegExp('^' + pattern.replace(/\*/g, '.*') + '$');
      return regex.test(name);
    });
    
    if (shouldDelete) {
      deleteCookie(name);
    }
  });
}

/**
 * Clear localStorage items matching patterns
 */
export function clearLocalStorageByPattern(patterns: string[]) {
  const keysToDelete: string[] = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    
    const shouldDelete = patterns.some(pattern => {
      const regex = new RegExp('^' + pattern.replace(/\*/g, '.*') + '$');
      return regex.test(key);
    });
    
    if (shouldDelete) {
      keysToDelete.push(key);
    }
  }
  
  keysToDelete.forEach(key => {
    localStorage.removeItem(key);
  });
}

/**
 * Clear sessionStorage items matching patterns
 */
export function clearSessionStorageByPattern(patterns: string[]) {
  const keysToDelete: string[] = [];
  
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (!key) continue;
    
    const shouldDelete = patterns.some(pattern => {
      const regex = new RegExp('^' + pattern.replace(/\*/g, '.*') + '$');
      return regex.test(key);
    });
    
    if (shouldDelete) {
      keysToDelete.push(key);
    }
  }
  
  keysToDelete.forEach(key => {
    sessionStorage.removeItem(key);
  });
}

/**
 * Cookie patterns for various third-party services
 */
export const COOKIE_PATTERNS = {
  // Google Analytics
  googleAnalytics: {
    cookies: ['_ga', '_ga_*', '_gid', '_gat', '_gat_*', '__utma', '__utmb', '__utmc', '__utmz', '__utmt', '__utmv', '_gac_*', 'test_analytics'],
    localStorage: ['_ga*', '_gid*', '_ga_test', 'amplitude_id'],
    sessionStorage: ['_ga*', '_gid*', '_ga_session']
  },
  
  // Google Tag Manager
  googleTagManager: {
    cookies: ['_gcl_*', '_gac_*'],
    localStorage: [],
    sessionStorage: []
  },
  
  // Google Ads
  googleAds: {
    cookies: ['_gcl_aw', '_gcl_dc', '_gcl_ha', '_gcl_gf', '_gcl_au', 'IDE', 'test_cookie', 'DSID', '__gads', '__gpi'],
    localStorage: [],
    sessionStorage: []
  },
  
  // Facebook Pixel
  facebook: {
    cookies: ['_fbp', '_fbc', 'fbm_*', 'xs', 'fr', 'datr', 'sb', 'wd', 'act', 'c_user', 'presence', 'test_marketing'],
    localStorage: ['_fbp_data'],
    sessionStorage: ['marketing_session']
  },
  
  // Twitter
  twitter: {
    cookies: ['_twitter_sess', 'personalization_id', 'guest_id', 'ct0', 'gt'],
    localStorage: [],
    sessionStorage: []
  },
  
  // LinkedIn
  linkedin: {
    cookies: ['li_*', 'lidc', 'bcookie', 'bscookie', 'lissc', 'UserMatchHistory', 'AnalyticsSyncHistory'],
    localStorage: [],
    sessionStorage: []
  },
  
  // Mixpanel
  mixpanel: {
    cookies: ['mp_*'],
    localStorage: ['mp_*', '__mp*'],
    sessionStorage: ['mp_*']
  },
  
  // Segment
  segment: {
    cookies: ['ajs_*'],
    localStorage: ['ajs_*'],
    sessionStorage: ['ajs_*']
  },
  
  // Amplitude
  amplitude: {
    cookies: ['amplitude_*'],
    localStorage: ['amplitude_*'],
    sessionStorage: ['amplitude_*']
  },
  
  // Hotjar
  hotjar: {
    cookies: ['_hj*', 'test_preferences'],
    localStorage: ['_hj*'],
    sessionStorage: ['_hj*', 'hotjar_session']
  },
  
  // Intercom
  intercom: {
    cookies: ['intercom-*', 'intercom-id'],
    localStorage: ['intercom-*', 'intercom-state'],
    sessionStorage: ['intercom-*']
  },
  
  // Drift
  drift: {
    cookies: ['driftt_*'],
    localStorage: ['drift*'],
    sessionStorage: ['drift*']
  },
  
  // Crisp
  crisp: {
    cookies: ['crisp-client*'],
    localStorage: ['crisp*'],
    sessionStorage: ['crisp*']
  },
  
  // HubSpot
  hubspot: {
    cookies: ['__hs*', 'hubspotutk', '__hssrc', '__hssc', '__hstc'],
    localStorage: [],
    sessionStorage: []
  },
  
  // Mailchimp
  mailchimp: {
    cookies: ['_mcid'],
    localStorage: [],
    sessionStorage: []
  },
  
  // YouTube
  youtube: {
    cookies: ['YSC', 'VISITOR_INFO1_LIVE', 'PREF', 'GPS', 'CONSENT'],
    localStorage: [],
    sessionStorage: []
  },
  
  // Vimeo
  vimeo: {
    cookies: ['vuid', 'player'],
    localStorage: [],
    sessionStorage: []
  },
  
  // Stripe
  stripe: {
    cookies: ['__stripe_*'],
    localStorage: ['__stripe*'],
    sessionStorage: ['__stripe*']
  },
  
  // PayPal
  paypal: {
    cookies: ['paypal*'],
    localStorage: [],
    sessionStorage: []
  },
  
  // Cloudflare
  cloudflare: {
    cookies: ['__cf*', '__cfduid'],
    localStorage: [],
    sessionStorage: []
  },
  
  // Optimizely
  optimizely: {
    cookies: ['optimizely*'],
    localStorage: ['optimizely*'],
    sessionStorage: ['optimizely*']
  },
  
  // Crazy Egg
  crazyEgg: {
    cookies: ['_ce*'],
    localStorage: [],
    sessionStorage: []
  },
  
  // FullStory
  fullstory: {
    cookies: ['fs_*', '_fs_*'],
    localStorage: ['fs_*'],
    sessionStorage: ['fs_*']
  },
  
  // Heap Analytics
  heap: {
    cookies: ['_hp*'],
    localStorage: ['_heap*'],
    sessionStorage: ['_heap*']
  },
  
  // Mouseflow
  mouseflow: {
    cookies: ['mf_*'],
    localStorage: [],
    sessionStorage: []
  }
};

/**
 * Get all cookie patterns for a specific category
 */
export function getCookiePatternsForCategory(category: 'analytics' | 'marketing' | 'preferences'): {
  cookies: string[];
  localStorage: string[];
  sessionStorage: string[];
} {
  let patterns = {
    cookies: [] as string[],
    localStorage: [] as string[],
    sessionStorage: [] as string[]
  };
  
  switch (category) {
    case 'analytics':
      // Analytics services
      const analyticsServices = [
        'googleAnalytics',
        'googleTagManager',
        'mixpanel',
        'segment',
        'amplitude',
        'heap',
        'fullstory'
      ];
      
      analyticsServices.forEach(service => {
        const servicePatterns = COOKIE_PATTERNS[service as keyof typeof COOKIE_PATTERNS];
        if (servicePatterns) {
          patterns.cookies.push(...servicePatterns.cookies);
          patterns.localStorage.push(...servicePatterns.localStorage);
          patterns.sessionStorage.push(...servicePatterns.sessionStorage);
        }
      });
      break;
      
    case 'marketing':
      // Marketing services
      const marketingServices = [
        'googleAds',
        'facebook',
        'twitter',
        'linkedin',
        'hubspot',
        'mailchimp',
        'optimizely',
        'crazyEgg'
      ];
      
      marketingServices.forEach(service => {
        const servicePatterns = COOKIE_PATTERNS[service as keyof typeof COOKIE_PATTERNS];
        if (servicePatterns) {
          patterns.cookies.push(...servicePatterns.cookies);
          patterns.localStorage.push(...servicePatterns.localStorage);
          patterns.sessionStorage.push(...servicePatterns.sessionStorage);
        }
      });
      break;
      
    case 'preferences':
      // Preference services
      const preferenceServices = [
        'hotjar',
        'intercom',
        'drift',
        'crisp',
        'mouseflow',
        'youtube',
        'vimeo'
      ];
      
      preferenceServices.forEach(service => {
        const servicePatterns = COOKIE_PATTERNS[service as keyof typeof COOKIE_PATTERNS];
        if (servicePatterns) {
          patterns.cookies.push(...servicePatterns.cookies);
          patterns.localStorage.push(...servicePatterns.localStorage);
          patterns.sessionStorage.push(...servicePatterns.sessionStorage);
        }
      });
      break;
  }
  
  // Remove duplicates using Array.from for proper TypeScript Set iteration
  patterns.cookies = Array.from(new Set(patterns.cookies));
  patterns.localStorage = Array.from(new Set(patterns.localStorage));
  patterns.sessionStorage = Array.from(new Set(patterns.sessionStorage));
  
  return patterns;
}

/**
 * Delete all cookies and storage for a category
 */
export function deleteAllDataForCategory(category: 'analytics' | 'marketing' | 'preferences') {
  const patterns = getCookiePatternsForCategory(category);
  
  // Delete cookies
  deleteCookiesByPattern(patterns.cookies);
  
  // Clear localStorage
  clearLocalStorageByPattern(patterns.localStorage);
  
  // Clear sessionStorage
  clearSessionStorageByPattern(patterns.sessionStorage);
}

/**
 * Test function to verify cookie deletion works
 */
export function testCookieDeletion() {
  // Set test cookies
  document.cookie = 'test_analytics=1; path=/';
  document.cookie = '_ga_test=123; path=/';
  localStorage.setItem('_ga_test', 'test');
  sessionStorage.setItem('_ga_test', 'test');
  
  // Delete them
  deleteAllDataForCategory('analytics');
  
  // Verify deletion
  const analyticsDeleted = !document.cookie.includes('test_analytics') && 
                          !document.cookie.includes('_ga_test') &&
                          !localStorage.getItem('_ga_test') &&
                          !sessionStorage.getItem('_ga_test');
  
  return analyticsDeleted;
}